import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { UserEntityInfo } from './Dashboard';

interface AllEntity {
  id: number;
  name: string;
  entity_type_id: number;
  entity_type_name: string;
  category: string;
}

interface DashboardFiltersProps {
  onEntitySelect?: (entity: AllEntity) => void;
  userEntities?: UserEntityInfo[];
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  onEntitySelect,
  userEntities = [],
}) => {
  const [selectedSearchEntity, setSelectedSearchEntity] = useState<AllEntity | null>(null);

  // Convert user entities to AllEntity format for search
  const allEntities: AllEntity[] = userEntities.map(e => ({
    id: e.id,
    name: e.name,
    entity_type_id: e.entity_type_id,
    entity_type_name: e.entity_type_display_label || e.entity_type_name,
    category: e.category,
  }));

  const handleSearchSelect = (_event: React.SyntheticEvent, value: AllEntity | null) => {
    setSelectedSearchEntity(value);
    if (value && onEntitySelect) {
      onEntitySelect(value);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Quick Search */}
      <Typography variant="subtitle2" color="text.secondary">
        Search Entity
      </Typography>
      <Autocomplete
        size="small"
        options={allEntities}
        value={selectedSearchEntity}
        onChange={handleSearchSelect}
        getOptionLabel={(option) => option.name}
        groupBy={(option) => `${option.category} - ${option.entity_type_name}`}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search entities..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.entity_type_name}
              </Typography>
            </Box>
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(options, { inputValue }) => {
          const filterValue = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              option.name.toLowerCase().includes(filterValue) ||
              option.entity_type_name.toLowerCase().includes(filterValue)
          );
        }}
        noOptionsText="No entities found"
      />
    </Box>
  );
};

export default DashboardFilters;
